import React from "react";
import { EntityRegistry, EntityRelationsManager } from "../EntityRelationsManager";
import { AddonParams, BaseAddon } from "../../base/BaseAddon";
import { StepChangeEvent, StepType } from "../../../types/types";
import toastService from "../../../../../../core/toaster/ToasterService";
import { TOASTER_TOAST_TIME } from "../../../../../../core/toaster/Toaster_model";
import "./_entity-relation-validation.scss";
import { EntityRelationAddon } from "../EntityRelationAddon";
import { EntityValidationComponent } from "./EntityRelationValidation_view";
import { NoDataFound } from "../../../components/NotFound/NoDataFound";

interface EntityRelationValidationAddonProps extends AddonParams {
  entityRegistry: EntityRegistry;
  entityRelationAddons: EntityRelationAddon[];
}

export class EntityRelationValidationAddon extends BaseAddon {
  private entityRegistry: EntityRegistry;
  private entityRelationAddons: EntityRelationAddon[];
  private entityManager: EntityRelationsManager | null = null;
  private currentEntityAddon: EntityRelationAddon | null = null;
  private processedEntities: Record<string, any[]> = {};
  private onBack: () => void;

  constructor(
    onNext: (event: StepChangeEvent) => void,
    onBack: () => void,
    params: EntityRelationValidationAddonProps
  ) {
    super("EntityRelationValidation", [StepType.entityRelationValidation], onNext, params);
    this.entityRegistry = params.entityRegistry;
    this.entityRelationAddons = params.entityRelationAddons || [];
    this.onBack = onBack;
  }

  protected getJSXElement(step: StepType, state: any): React.ReactElement {
    if (!this.entityManager && this.entityRelationAddons.length > 0) {
      this.entityManager = new EntityRelationsManager(
        this.entityRegistry,
        this.entityRelationAddons,
        state.data || [],
        this.handleEntityResolutionComplete
      );

      this.currentEntityAddon = this.entityManager.startEntityResolution();
    }

    if (!state.data) {
      return <NoDataFound onBack={this.onBack}></NoDataFound>;
    }

    return (
      <EntityValidationComponent
        entityRegistry={this.entityRegistry}
        entityAddons={this.entityRelationAddons}
        uploadedData={state.data || []}
        currentEntity={this.currentEntityAddon}
        processedEntities={this.processedEntities}
        onEntityProcessed={this.handleEntityProcessed}
        onContinue={() => this.handleContinue(state)}
        onBack={this.onBack}
      />
    );
  }

  private handleEntityProcessed = (entityType: string, result: any[]) => {
    this.processedEntities[entityType] = result;

    if (this.entityManager) {
      this.currentEntityAddon = this.entityManager.processEntityResult(entityType, result);

      this.forceUpdate();
    }
  };

  private handleEntityResolutionComplete = (processedEntities: Record<string, any[]>) => {
    this.processedEntities = processedEntities;
    this.currentEntityAddon = null;
  };

  private handleContinue = (state: any) => {
    try {
      this.onNext({
        type: StepType.validateData,
        data: state.data,
        headerValues: state.headerValues,
        columns: state.columns,
        entityRelations: this.processedEntities
      });
    } catch (e) {
      console.error("Error in entity validation:", e);
      toastService
        .showErrorToast(TOASTER_TOAST_TIME.NORMAL, "right")
        .setContent(<span>{e?.message || "An error occurred processing entity relations"}</span>);
    }
  };

  private forceUpdate() {}
}
